import { useState, useRef, useEffect } from "react";
import { Link } from "wouter";
import { Search, ArrowLeft, Calendar, Clock, Car as CarIcon, CheckCircle2, Loader2, Image as ImageIcon, Droplets, Upload, QrCode, Gift, Star, TrendingUp, Menu, Home, Package, Phone, MessageSquare, HelpCircle, Briefcase, FileText, User as UserIcon, LogIn, LogOut, ImageIcon as GalleryIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { getStatusLabel } from "@/lib/utils";
import { formatCurrency } from "@/lib/utils";
import { WashStatus } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Html5Qrcode } from "html5-qrcode";
import { requestGuestFCMToken } from "@/lib/guestNotifications";

export default function Track() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [reservationCode, setReservationCode] = useState("");
  const [plate, setPlate] = useState("");
  const [searching, setSearching] = useState(false);
  const [wash, setWash] = useState<any>(null);
  const [vehicle, setVehicle] = useState<any>(null);
  const [services, setServices] = useState<any[]>([]);
  const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
  const [uploadingQR, setUploadingQR] = useState(false);
  const [autoSearched, setAutoSearched] = useState(false);
  const [notificationBannerDismissed, setNotificationBannerDismissed] = useState(false);
  const [enablingNotifications, setEnablingNotifications] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-search from URL parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const codeParam = urlParams.get('code');
    const plateParam = urlParams.get('plate');

    if (codeParam && plateParam && !autoSearched) {
      setReservationCode(codeParam.toUpperCase());
      setPlate(plateParam.toUpperCase());
      setAutoSearched(true);
      
      // Trigger search automatically
      setTimeout(() => {
        handleSearch(codeParam.toUpperCase(), plateParam.toUpperCase());
      }, 100);
    } else if (codeParam && !autoSearched) {
      // If only code is provided, just populate the field
      setReservationCode(codeParam.toUpperCase());
    }
  }, [autoSearched]);
  
  // Handle QR code file upload
  const handleQRUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploadingQR(true);
    
    try {
      const html5QrCode = new Html5Qrcode("qr-reader-hidden");
      const result = await html5QrCode.scanFile(file, false);
      
      // Parse the URL to extract reservation code
      // Expected format: https://temizfilo.web.app/track?code=ABC123
      const url = new URL(result);
      const code = url.searchParams.get('code');
      
      if (code) {
        setReservationCode(code.toUpperCase());
        toast({
          title: "QR Kod Okundu",
          description: `Rezervasyon kodu: ${code.toUpperCase()}`,
        });
      } else {
        toast({
          title: "Geçersiz QR Kod",
          description: "QR kod rezervasyon kodu içermiyor",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Okuma Hatası",
        description: "QR kod okunamadı. Lütfen geçerli bir QR kod resmi yükleyin.",
        variant: "destructive",
      });
    } finally {
      setUploadingQR(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSearch = async (searchCode?: string, searchPlate?: string) => {
    // Use provided parameters or state values
    const codeToSearch = searchCode || reservationCode;
    const plateToSearch = searchPlate || plate;

    if (!codeToSearch || !plateToSearch) {
      toast({
        title: "Hata",
        description: "Lütfen rezervasyon kodu ve plakayı girin",
        variant: "destructive",
      });
      return;
    }

    setSearching(true);
    try {
      // Search for wash/appointment with reservation code
      const washQuery = query(
        collection(db, "washes"),
        where("reservationCode", "==", codeToSearch.toUpperCase())
      );
      const washSnapshot = await getDocs(washQuery);

      if (washSnapshot.empty) {
        // Try appointments
        const appointmentQuery = query(
          collection(db, "appointments"),
          where("reservationCode", "==", codeToSearch.toUpperCase())
        );
        const appointmentSnapshot = await getDocs(appointmentQuery);

        if (appointmentSnapshot.empty) {
          toast({
            title: "Bulunamadı",
            description: "Bu rezervasyon koduna ait kayıt bulunamadı",
            variant: "destructive",
          });
          setWash(null);
          setVehicle(null);
          setServices([]);
          setSearching(false);
          return;
        }

        const appointment = { id: appointmentSnapshot.docs[0].id, ...appointmentSnapshot.docs[0].data() } as any;
        
        // Get vehicle
        const vehicleSnapshot = await getDocs(
          query(collection(db, "vehicles"), where("__name__", "==", appointment.vehicleId))
        );
        
        if (vehicleSnapshot.empty || vehicleSnapshot.docs[0].data().plate.toUpperCase().replace(/\s/g, '') !== plateToSearch.toUpperCase().replace(/\s/g, '')) {
          toast({
            title: "Eşleşme Hatası",
            description: "Plaka ile rezervasyon kodu eşleşmiyor",
            variant: "destructive",
          });
          setWash(null);
          setVehicle(null);
          setServices([]);
          setSearching(false);
          return;
        }

        const vehicleData = { id: vehicleSnapshot.docs[0].id, ...vehicleSnapshot.docs[0].data() };

        // Get services - handle missing or empty serviceIds
        const servicesSnapshot = await getDocs(query(collection(db, "services")));
        const allServices = servicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const serviceIds = appointment.serviceIds || (appointment.serviceId ? [appointment.serviceId] : []);
        const appointmentServices = allServices.filter(s => serviceIds.includes(s.id));

        setWash({ ...appointment, isAppointment: true, serviceIds: serviceIds });
        setVehicle(vehicleData);
        setServices(appointmentServices);
      } else {
        const washData = { id: washSnapshot.docs[0].id, ...washSnapshot.docs[0].data() } as any;
        
        // Get vehicle
        const vehicleSnapshot = await getDocs(
          query(collection(db, "vehicles"), where("__name__", "==", washData.vehicleId))
        );
        
        if (vehicleSnapshot.empty || vehicleSnapshot.docs[0].data().plate.toUpperCase().replace(/\s/g, '') !== plateToSearch.toUpperCase().replace(/\s/g, '')) {
          toast({
            title: "Eşleşme Hatası",
            description: "Plaka ile rezervasyon kodu eşleşmiyor",
            variant: "destructive",
          });
          setWash(null);
          setVehicle(null);
          setServices([]);
          setSearching(false);
          return;
        }

        const vehicleData = { id: vehicleSnapshot.docs[0].id, ...vehicleSnapshot.docs[0].data() };

        // Get services - handle missing or empty serviceIds
        const servicesSnapshot = await getDocs(query(collection(db, "services")));
        const allServices = servicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const serviceIds = washData.serviceIds || (washData.serviceId ? [washData.serviceId] : []);
        const washServices = allServices.filter(s => serviceIds.includes(s.id));

        setWash({ ...washData, isAppointment: false, serviceIds: serviceIds });
        setVehicle(vehicleData);
        setServices(washServices);
      }
    } catch (error: any) {
      console.error("❌ Track search error:", error);
      const errorMessage = error?.message || "Arama sırasında bir hata oluştu";
      toast({
        title: "Hata",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSearching(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case WashStatus.PENDING:
        return <Badge variant="secondary">Beklemede</Badge>;
      case WashStatus.CONFIRMED:
        return <Badge variant="default">Onaylandı</Badge>;
      case WashStatus.QUEUED:
        return <Badge variant="default" className="bg-blue-500">Sırada</Badge>;
      case WashStatus.IN_PROGRESS:
        return <Badge variant="default" className="bg-orange-500 animate-pulse">İşlemde</Badge>;
      case WashStatus.COMPLETED:
        return <Badge variant="default" className="bg-green-500">Tamamlandı</Badge>;
      case WashStatus.CANCELLED:
        return <Badge variant="destructive">İptal</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getProgressPercentage = (status: string) => {
    switch (status) {
      case WashStatus.PENDING: return 20;
      case WashStatus.CONFIRMED: return 40;
      case WashStatus.QUEUED: return 60;
      case WashStatus.IN_PROGRESS: return 80;
      case WashStatus.COMPLETED: return 100;
      default: return 0;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link href="/">
              <div className="flex items-center gap-2 cursor-pointer hover-elevate active-elevate-2 px-3 py-2 rounded-lg transition-all">
                <Droplets className="w-8 h-8 text-primary" />
                <span className="text-xl font-bold text-primary">TemizFilo</span>
              </div>
            </Link>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <Link href="/" className="text-sm font-medium hover:text-primary transition-colors">
                Ana Sayfa
              </Link>
              <Link href="/book" className="text-sm font-medium hover:text-primary transition-colors">
                Randevu Al
              </Link>
              <Link href="/services" className="text-sm font-medium hover:text-primary transition-colors">
                Hizmetler
              </Link>
              <Link href="/subscriptions" className="text-sm font-medium hover:text-primary transition-colors">
                Abonelikler
              </Link>
            </div>
            
            <div className="flex items-center gap-2">
              {user && (
                <Link href={user.role === "customer" ? "/customer" : `/${user.role}`} className="hidden sm:block">
                  <Button variant="outline" size="sm" data-testid="button-dashboard">
                    {user.role === "customer" ? "Panelim" : 
                     user.role === "employee" ? "Çalışan Paneli" :
                     user.role === "cashier" ? "Kasiyer Paneli" :
                     "Admin Paneli"}
                  </Button>
                </Link>
              )}
              <ThemeToggle />
              
              {/* Mobile Menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild className="md:hidden">
                  <Button variant="ghost" size="icon" aria-label="Menüyü aç" data-testid="button-mobile-menu">
                    <Menu className="w-6 h-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] sm:w-[400px] flex flex-col">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                      <Droplets className="w-6 h-6 text-primary" />
                      <span>TemizFilo</span>
                    </SheetTitle>
                  </SheetHeader>
                  <div className="flex flex-col gap-2 mt-6">
                    {user ? (
                      <>
                        <Link href={user.role === "customer" ? "/customer" : `/${user.role}`} onClick={() => setMobileMenuOpen(false)}>
                          <Button className="w-full justify-start" data-testid="mobile-link-dashboard">
                            <UserIcon className="w-4 h-4 mr-2" />
                            {user.role === "customer" ? "Panelim" : 
                             user.role === "employee" ? "Çalışan Paneli" :
                             user.role === "cashier" ? "Kasiyer Paneli" :
                             "Admin Paneli"}
                          </Button>
                        </Link>
                        <Link href="/book" onClick={() => setMobileMenuOpen(false)}>
                          <Button variant="outline" className="w-full justify-start" data-testid="mobile-link-book">
                            <Calendar className="w-4 h-4 mr-2" />
                            Randevu Al
                          </Button>
                        </Link>
                      </>
                    ) : (
                      <>
                        <Link href="/book" onClick={() => setMobileMenuOpen(false)}>
                          <Button className="w-full justify-start" data-testid="mobile-link-book">
                            <Calendar className="w-4 h-4 mr-2" />
                            Randevu Al
                          </Button>
                        </Link>
                        <Link href="/login" onClick={() => setMobileMenuOpen(false)}>
                          <Button variant="outline" className="w-full justify-start" data-testid="mobile-link-login">
                            <LogIn className="w-4 h-4 mr-2" />
                            Giriş Yap
                          </Button>
                        </Link>
                      </>
                    )}
                    <div className="h-px bg-border my-2" />
                    <Link href="/" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-home">
                        <Home className="w-4 h-4 mr-2" />
                        Ana Sayfa
                      </Button>
                    </Link>
                    <Link href="/services" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-services">
                        <Droplets className="w-4 h-4 mr-2" />
                        Hizmetler
                      </Button>
                    </Link>
                    <Link href="/gallery" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-gallery">
                        <GalleryIcon className="w-4 h-4 mr-2" />
                        Galeri
                      </Button>
                    </Link>
                    <Link href="/subscriptions" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-subscriptions">
                        <Package className="w-4 h-4 mr-2" />
                        Abonelikler
                      </Button>
                    </Link>
                    <Link href="/contact" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-contact">
                        <Phone className="w-4 h-4 mr-2" />
                        İletişim
                      </Button>
                    </Link>
                    <Link href="/feedback" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-feedback">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Şikayet ve Öneri
                      </Button>
                    </Link>
                    <Link href="/faq" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-faq">
                        <HelpCircle className="w-4 h-4 mr-2" />
                        SSS
                      </Button>
                    </Link>
                    <Link href="/corporate-quote" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-corporate">
                        <Briefcase className="w-4 h-4 mr-2" />
                        Kurumsal Teklif
                      </Button>
                    </Link>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-3">
              <Search className="w-12 h-12 text-primary" />
            </div>
            <h1 className="text-4xl font-bold text-foreground">Randevumu Takip Et</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Rezervasyon kodunuz ve plaka ile aracınızın yıkama durumunu anlık olarak takip edebilirsiniz
            </p>
          </div>

          {/* Search Form */}
          <Card className="p-8">
            <div className="space-y-6">
              {/* QR Code Upload Option */}
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                <div className="flex items-start gap-3 mb-3">
                  <QrCode className="w-5 h-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm mb-1">QR Kod ile Hızlı Sorgulama</h3>
                    <p className="text-xs text-muted-foreground">
                      Randevu aldığınızda size verilen QR kod resmini yükleyin
                    </p>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleQRUpload}
                  className="hidden"
                  id="qr-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingQR}
                  data-testid="button-upload-qr"
                >
                  {uploadingQR ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      QR Kod Okunuyor...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      QR Kod Yükle
                    </>
                  )}
                </Button>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    veya manuel girin
                  </span>
                </div>
              </div>

              <div>
                <Label htmlFor="code">Rezervasyon Kodu</Label>
                <Input
                  id="code"
                  placeholder="Örn: KJVKUR"
                  value={reservationCode}
                  onChange={(e) => setReservationCode(e.target.value.toUpperCase())}
                  className="mt-2 font-mono text-lg"
                  maxLength={6}
                  data-testid="input-code"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Randevu alırken size verilen 6 karakterlik kod
                </p>
              </div>

              <div>
                <Label htmlFor="plate">Plaka</Label>
                <Input
                  id="plate"
                  placeholder="Örn: 34 ABC 123"
                  value={plate}
                  onChange={(e) => setPlate(e.target.value.toUpperCase())}
                  className="mt-2 font-mono text-lg"
                  data-testid="input-plate"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Aracınızın plaka numarası
                </p>
              </div>

              <Button
                onClick={() => handleSearch()}
                disabled={searching || !reservationCode || !plate}
                className="w-full"
                size="lg"
                data-testid="button-search"
              >
                {searching ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Aranıyor...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Sorgula
                  </>
                )}
              </Button>
            </div>
          </Card>
          
          {/* Hidden div for Html5Qrcode */}
          <div id="qr-reader-hidden" style={{ display: 'none' }}></div>

          {/* Results */}
          {wash && vehicle && (
            <Card className="p-8">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-foreground">Durum Bilgisi</h2>
                  {getStatusBadge(wash.status)}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Rezervasyon Kodu</p>
                      <p className="text-lg font-mono font-bold">{wash.reservationCode}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Araç</p>
                      <p className="text-lg font-semibold">{vehicle.brand} {vehicle.model}</p>
                      <p className="text-sm font-mono text-muted-foreground">{vehicle.plate}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Hizmetler</p>
                      <p className="text-lg font-semibold">{services.map(s => s.name).join(", ")}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Tarih</p>
                      <p className="text-lg font-semibold">
                        {wash.scheduledDate} {wash.scheduledTime}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Tutar</p>
                      <p className="text-2xl font-bold text-primary">
                        {formatCurrency(wash.finalAmount || wash.totalAmount)}
                      </p>
                    </div>
                    {wash.status === WashStatus.COMPLETED && wash.completedAt && (
                      <div>
                        <p className="text-sm text-muted-foreground">Tamamlanma</p>
                        <p className="text-lg font-semibold">
                          {format(wash.completedAt, 'dd MMMM yyyy HH:mm', { locale: tr })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Progress */}
                <div className="mt-8">
                  <p className="text-sm text-muted-foreground mb-4">İşlem Durumu</p>
                  <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="absolute top-0 left-0 h-full bg-primary transition-all duration-500 ease-out"
                      style={{ width: `${getProgressPercentage(wash.status)}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                    <span>Alındı</span>
                    <span>Sırada</span>
                    <span>İşleniyor</span>
                    <span>Tamamlandı</span>
                  </div>
                </div>

                {/* Photos */}
                {!wash.isAppointment && wash.photos && wash.photos.length > 0 && (
                  <div>
                    <Button 
                      variant="outline" 
                      onClick={() => setPhotoGalleryOpen(true)}
                      data-testid="button-photos"
                    >
                      <ImageIcon className="w-4 h-4 mr-2" />
                      Fotoğrafları Görüntüle ({wash.photos.length})
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Notification Enable Banner - Show for guest users who haven't enabled notifications */}
          {wash && !user && !notificationBannerDismissed && (
            <Card className="bg-gradient-to-r from-blue-500/10 via-blue-400/5 to-blue-500/10 border-blue-500/20 overflow-hidden">
              <div className="p-6">
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    </div>
                  </div>
                  
                  <div className="flex-1 space-y-2">
                    <h3 className="text-lg font-semibold text-foreground">Anlık Güncellemeler Alın</h3>
                    <p className="text-sm text-muted-foreground">
                      Yıkama durumunuz değiştiğinde anında bildirim almak ister misiniz? (İsteğe bağlı)
                    </p>
                  </div>

                  <div className="flex items-center gap-2 sm:ml-auto">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setNotificationBannerDismissed(true)}
                      data-testid="button-dismiss-notifications"
                    >
                      Hayır
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={async () => {
                        setEnablingNotifications(true);
                        try {
                          const token = await requestGuestFCMToken();
                          if (token) {
                            toast({
                              title: "Bildirimler Etkin",
                              description: "Yıkama güncellemeleri için bildirim alacaksınız.",
                            });
                            setNotificationBannerDismissed(true);
                          } else {
                            toast({
                              title: "Bildirim İzni Gerekli",
                              description: "Tarayıcınızda bildirim izni verin.",
                              variant: "destructive",
                            });
                          }
                        } catch (error) {
                          toast({
                            title: "Bildirimler Devre Dışı",
                            description: "Tarayıcınız bildirimleri desteklemiyor veya izin vermediniz.",
                            variant: "destructive",
                          });
                        } finally {
                          setEnablingNotifications(false);
                        }
                      }}
                      disabled={enablingNotifications}
                      data-testid="button-enable-notifications"
                    >
                      {enablingNotifications ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Etkinleştiriliyor...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                          Bildirimleri Aç
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Registration Encouragement Banner - Show for completed washes when user is not logged in */}
          {wash && !user && wash.status === WashStatus.COMPLETED && (
            <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-primary/20 overflow-hidden" data-testid="registration-banner">
              <div className="p-8">
                <div className="flex flex-col md:flex-row items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                      <Gift className="w-8 h-8 text-primary" />
                    </div>
                  </div>
                  
                  <div className="flex-1 space-y-4">
                    <div>
                      <h3 className="text-2xl font-bold mb-2">Tebrikler! Yıkamanız Tamamlandı 🎉</h3>
                      <p className="text-muted-foreground">
                        Sadakat programımıza katılarak bir sonraki yıkamanızda puan kazanmaya başlayın!
                      </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="flex items-start gap-3 p-4 rounded-lg bg-background/50">
                        <Star className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-sm mb-1">Her Yıkamada Puan</p>
                          <p className="text-xs text-muted-foreground">1 yıkama = 1 puan</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3 p-4 rounded-lg bg-background/50">
                        <Gift className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-sm mb-1">Bedava Yıkama</p>
                          <p className="text-xs text-muted-foreground">5 puan = 1 bedava</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3 p-4 rounded-lg bg-background/50">
                        <TrendingUp className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-sm mb-1">Özel Avantajlar</p>
                          <p className="text-xs text-muted-foreground">VIP seviyeler & indirimler</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                      <Link href="/book">
                        <Button size="lg" className="w-full sm:w-auto" data-testid="button-register-now">
                          <Gift className="w-4 h-4 mr-2" />
                          Hemen Kayıt Ol
                        </Button>
                      </Link>
                      <Link href="/loyalty-benefits">
                        <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="button-learn-more">
                          Avantajları Keşfet
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Photo Gallery Dialog */}
      <Dialog open={photoGalleryOpen} onOpenChange={setPhotoGalleryOpen}>
        <DialogContent className="max-w-4xl" data-testid="dialog-photo-gallery">
          <DialogHeader>
            <DialogTitle>Yıkama Fotoğrafları</DialogTitle>
            <DialogDescription>
              Araç yıkama öncesi ve sonrası fotoğraflar
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {wash && wash.photos && wash.photos.length > 0 ? (
              <>
                {/* Before Photos */}
                {wash.photos.filter((p: any) => p.type === 'before').length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Önce</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {wash.photos.filter((p: any) => p.type === 'before').map((photo: any, idx: number) => (
                        <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-muted" data-testid={`photo-before-${idx}`}>
                          <img 
                            src={photo.url} 
                            alt={`Önce ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* After Photos */}
                {wash.photos.filter((p: any) => p.type === 'after').length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Sonra</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {wash.photos.filter((p: any) => p.type === 'after').map((photo: any, idx: number) => (
                        <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-muted" data-testid={`photo-after-${idx}`}>
                          <img 
                            src={photo.url} 
                            alt={`Sonra ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Henüz fotoğraf yüklenmemiş</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setPhotoGalleryOpen(false)}
              data-testid="button-close-gallery"
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
